import React from "react";
import { LoaderFunctionArgs, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

import { Tabs } from "@performyard/py-components";

import Breadcrumbs from "../../components/Breadcrumbs";

import { useAiSettings, useCurrentUser, useInternalApiUser, useRbacEmployeePermissions } from "../../api/hooks";
import { useIsNewNavigation } from "../../api/hooks/defs/navigation";
import { useEmployeeDashboardOrigin } from "../../hooks/useEmployeeDashboardOrigin";
import { useFeatureFlagQuery } from "../../hooks/useFeatureFlag";

import {
	currentUserQuery,
	emailTokensQuery,
	employeeCustomFieldsQuery,
	getInternalApiUserQuery,
	rbacEmployeePermissionsQuery,
} from "../../api/queries";
import { SimplifiedQuery, dataLoader } from "../../api/queries/dataLoader";

import { buildEmployeeDetailsBreadcrumbs } from "../../utils/employeeDetailsBreadcrumbs";
import { readTargetCompanyId } from "../../utils/targetCompanyId";

import BasePageTemplate from "../../pageTemplates/BasePageTemplate";
import ErrorPage from "../Error/ErrorPage";

const TAB_PATHS = ["details", "insights"] as const;
type TabPath = (typeof TAB_PATHS)[number];

const EMPLOYEE_SUMMARY_VIEW_PERMISSIONS = new Set([
	"employee_summary:viewSelf",
	"employee_summary:viewAsManager",
	"employee_summary:viewAsAdmin",
]);

export const viewEmployeeDetailsLoader = async ({ params, request }: LoaderFunctionArgs) => {
	const queries: Array<() => SimplifiedQuery> = [];
	// Skip the employee preloads entirely when the URL names another company: those requests are
	// scoped to the caller's own company, so they would fail and reject the loader, which the
	// router renders as the generic crash page. The layout renders a 403 instead.
	const targetCompanyId = readTargetCompanyId(new URL(request.url).search);

	if (params?.userID && !targetCompanyId) {
		queries.push(() => rbacEmployeePermissionsQuery(params?.userID || "", "employee_details"));
		queries.push(() => rbacEmployeePermissionsQuery(params?.userID || "", "dashboard"));
		queries.push(() => rbacEmployeePermissionsQuery(params?.userID || "", "goal"));
		queries.push(() => employeeCustomFieldsQuery(params?.userID || ""));
		queries.push(() => getInternalApiUserQuery(params?.userID || ""));
		queries.push(() => emailTokensQuery(params?.userID || ""));
	}

	return await dataLoader([[currentUserQuery], queries]);
};

const EmployeeDetailsLayout: React.FC = () => {
	const { userID = "" } = useParams<{ userID: string }>();
	const location = useLocation();
	const navigate = useNavigate();

	const { data: currentUser } = useCurrentUser();
	const currentUserId = currentUser?.me?.authorization?.payload?.user_id || "";
	const currentCompanyId = currentUser?.me?.authorization?.payload?.company_id || "";
	const userId = userID || currentUserId || "";

	const targetCompanyId = readTargetCompanyId(location.search);
	// Gated on a resolved own-company too: `currentCompanyId` is "" until `useCurrentUser` settles,
	// which would otherwise read every named company — including the caller's own — as cross-tenant.
	const isCrossTenant = !!currentCompanyId && !!targetCompanyId && targetCompanyId !== currentCompanyId;

	const { data: userInfo } = useInternalApiUser(userId, { enabled: !!userId && !isCrossTenant });

	const { data: insightsPermissions, isLoading: isInsightsPermissionsLoading } = useRbacEmployeePermissions(
		userId,
		"employee_summary",
		!userId || isCrossTenant
	);
	const { value: coachingFlagEnabled, query: coachingFlagQuery } = useFeatureFlagQuery(
		"ff-is-coaching-enabled",
		false
	);
	const { data: aiSettings, isLoading: isAiSettingsLoading } = useAiSettings();

	const hasInsightsPermission =
		insightsPermissions?.some((permission) => EMPLOYEE_SUMMARY_VIEW_PERMISSIONS.has(permission)) ?? false;

	const isGateLoading = isInsightsPermissionsLoading || coachingFlagQuery.isLoading || isAiSettingsLoading;

	// Gate the tab on the Coaching FF — the same flag the admin AI Insights > Employees tab requires,
	// so admin-can-configure ⟺ tab-appears — plus the "Employee Insights" AI setting + RBAC.
	// employeeCoachingEnabled gates only the Coaching *section* (EmployeeInsights/config.ts),
	// not the tab.
	const canViewInsights =
		hasInsightsPermission && coachingFlagEnabled === true && aiSettings?.employeeInsightsEnabled === true;

	const lastSegment = location.pathname.split("/").pop() ?? "";
	const activeTab: TabPath = TAB_PATHS.includes(lastSegment as TabPath) ? (lastSegment as TabPath) : "details";

	// The layout owns the breadcrumbs (they sit above both the Details and Insights tabs).
	// New-nav roots the crumb on where the user came from (another employee's dashboard or a
	// recognized list page); legacy nav keeps the static Dashboard > Employee Details chain.
	const isNewNav = useIsNewNavigation();
	// Deep: a Details page reached from another employee's dashboard carries that origin nested
	// inside its own `from` when the user follows a manager/custom-field link onward.
	const dashboardOrigin = useEmployeeDashboardOrigin({ deep: true });
	const breadcrumbs = isNewNav
		? buildEmployeeDetailsBreadcrumbs({ search: location.search, dashboardOrigin, userId })
		: [
				{ pageID: "dashboard", label: "Dashboard", url: currentUserId === userID ? "/" : `/user/${userId}` },
				{ pageID: "employee-details", label: "Employee Details", url: `/details/${userId}` },
			];

	const handleTabChange = (value: string) => {
		if (!TAB_PATHS.includes(value as TabPath)) {
			return;
		}
		// "details" is the index route, so it has no path segment of its own.
		navigate(value === "details" ? `/details/${userId}` : `/details/${userId}/${value}`);
	};

	const tabItems = [
		{
			value: "details" as const,
			label: "Details",
			content: <></>,
		},
		...(canViewInsights
			? [
					{
						value: "insights" as const,
						label: "Employee Insights",
						content: <></>,
					},
				]
			: []),
	];

	// Employee Details is deliberately not a cross-company surface. A super user looking at another
	// company takes over the employee's account instead, which is how this worked before the side
	// nav pointed employee name links here (PY-13817).
	if (isCrossTenant) {
		return <ErrorPage code={403} />;
	}

	if (activeTab === "insights" && !canViewInsights && !isGateLoading) {
		return <ErrorPage code={403} />;
	}

	return (
		<BasePageTemplate title={`Details - ${userInfo?.name}`} backgroundColor="grey">
			<div className="flex flex-col items-center">
				<div className="w-[880px] max-w-full">
					<Breadcrumbs pages={breadcrumbs} />
				</div>
			</div>
			<div className="mt-6 flex flex-col items-center">
				<div className="w-[880px] max-w-full">
					<Tabs
						size="large"
						value={activeTab}
						color={"neutral"}
						onValueChange={handleTabChange}
						tabs={tabItems}
					/>
				</div>
			</div>
			{(activeTab !== "insights" || canViewInsights) && <Outlet />}
		</BasePageTemplate>
	);
};

export default EmployeeDetailsLayout;
